import { Skeleton } from "@/components/ui/skeleton";
import type { OfferPriceState } from "@/utils/offerPriceState";
import { AlertTriangle } from "lucide-react";

/**
 * What a price slot shows in place of a price when it must not show one yet
 * (`loading`) or cannot (`error`). See `OfferPriceState` (KAN-343).
 */
export function PriceLoader({ className = "" }: { className?: string }) {
  return (
    <Skeleton
      role="status"
      aria-label="Loading price"
      className={`inline-block h-4 w-14 align-middle ${className}`}
    />
  );
}

export function PriceError({
  showLabel = true,
  onDark = false,
  className = ""
}: {
  /** On the dialog's purple header the usual red does not read. */
  onDark?: boolean;
  /** Icon only when the slot is too narrow for the words; the label stays for screen readers. */
  showLabel?: boolean;
  className?: string;
}) {
  return (
    <span
      role="alert"
      title="Price unavailable"
      className={`inline-flex items-center gap-1 whitespace-nowrap font-medium ${onDark ? "text-red-400" : "text-red-600"} ${className}`}
    >
      <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span className={showLabel ? undefined : "sr-only"}>
        Price unavailable
      </span>
    </span>
  );
}

/** The loader or error for a non-`ready` state; `null` when the price may show. */
export function PriceStatusSlot({
  state,
  showLabel,
  onDark,
  className
}: {
  state: OfferPriceState;
  showLabel?: boolean;
  onDark?: boolean;
  className?: string;
}) {
  if (state === "loading")
    return (
      <PriceLoader
        className={`${onDark ? "bg-white/20" : ""} ${className ?? ""}`}
      />
    );
  if (state === "error")
    return (
      <PriceError showLabel={showLabel} onDark={onDark} className={className} />
    );
  return null;
}
